import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BaseUrlService } from '../services/url-service';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { ConfigService } from 'src/app/shared/services/multi-clients/config.service';
import { UrlService } from 'src/app/shared/services/url.service';

export interface BackendParam {
    name: string;
    value: string;
}

export interface BackendError {
    errorKey: string;
    errorMessage: string;
}

export interface BackendResponse<T> {
    success: boolean;
    errors: Array<BackendError>;
    data: T;
}

@Injectable()
export class BackendService {
    httpRaw: HttpClient;

    constructor(
            private http: HttpClient,
            private urlService: BaseUrlService,
            rawHandler: HttpBackend,
            private _configService: ConfigService,
    ) {
        this.httpRaw = new HttpClient(rawHandler);
    }

    private getHttpClient(disableInterceptors): HttpClient {
        return disableInterceptors ? this.httpRaw:this.http;
    }

    private parseParams(params: Array<BackendParam>): HttpParams {
        let httpParams: HttpParams = new HttpParams();

        params.forEach(x => {
            httpParams = httpParams.append(x.name, x.value);
        });

        return httpParams;
    }

    private addHeaders() {
        const headers = new HttpHeaders();
        headers.append('Content-Type', 'application/json');
        return headers;
    }

    public get<T>(
            urlKey: string,
            urlParts: Array<string> = [],
            params: Array<BackendParam> = [],
            disableInterceptors: any = false,
            withCredentials: boolean = false,
            options: { headers: HttpHeaders, observe: 'body', responseType: 'json' } = null
    ): Observable<BackendResponse<T>> {

        const queryString = this.parseParams(params);
        const headers = options != null ? options.headers:this.addHeaders();

        let isExporting = localStorage.getItem("pdfExporting") === "true";
        
        if (isExporting && !_.isNil(this._configService.settings.apiUrl) && !_.isEmpty(this._configService.settings.apiUrlTG4)) {
            return this.getHttpClient(disableInterceptors).get<BackendResponse<T>>(
                    this.urlService.getUrl(urlKey, ...urlParts).replace(this._configService.settings.apiUrl, this._configService.settings.apiUrlTG4),
                    {
                        headers: headers,
                        withCredentials: withCredentials,
                        params: queryString,
                        observe: options != null ? options.observe:null,
                        responseType: options != null ? options.responseType:null
                    }
            );
        }
        return this.getHttpClient(disableInterceptors).get<BackendResponse<T>>(
                this.urlService.getUrl(urlKey, ...urlParts),
                {
                    headers: headers,
                    withCredentials: withCredentials,
                    params: queryString,
                    observe: options != null ? options.observe:null,
                    responseType: options != null ? options.responseType:null
                }
        );
    }

    public post<T>(urlKey: string, data: any, disableInterceptors = false): Observable<BackendResponse<T>> {
        const headers = this.addHeaders();
        return this.getHttpClient(disableInterceptors).post<BackendResponse<T>>(
                this.urlService.getUrl(urlKey),
                data,
                {
                    headers: headers
                }
        );
    }

    public put<T>(urlKey: string, data: any, disableInterceptors = false): Observable<BackendResponse<T>> {
        const headers = this.addHeaders();
        return this.getHttpClient(disableInterceptors).put<BackendResponse<T>>(
                this.urlService.getUrl(urlKey),
                data,
                {
                    headers: headers
                }
        );
    }

    public delete<T>(
            urlKey: string,
            params: Array<BackendParam> = [],
            disableInterceptors = false
    ): Observable<BackendResponse<T>> {
        const parsedParams = this.parseParams(params);
        const headers = this.addHeaders();

        return this.getHttpClient(disableInterceptors).delete<
                BackendResponse<T>
        >(this.urlService.getUrl(urlKey), {
            headers: headers,
            params: parsedParams
        });
    }

    public deleteMany<T>(
            urlKey: string,
            params: HttpParams,
            disableInterceptors = false
    ): Observable<BackendResponse<T>> {
        const headers = this.addHeaders();

        return this.getHttpClient(disableInterceptors).delete<
                BackendResponse<T>
        >(this.urlService.getUrl(urlKey), {
            headers: headers,
            params: params
        });
    }

    getParams(object: any): HttpParams {

        let params = new HttpParams();
        Object.keys(object).forEach((key) => {
            if (object[key]) {
                if (object[key] instanceof Array) {
                    object[key].forEach((element: any) => {
                        params = params.append(key.toString(), element);
                    });
                } else if (typeof object[key] === 'object') {
                    Object.keys(object[key]).forEach((k) => {
                        params = params.append(key.toString() + '.' + k.toString(), object[key][k]);
                    });
                } else {
                    params = params.set(key.toString(), object[key].toString());
                }
            }
        });
        return params;
    }
}
